
import Vue from 'vue';
import { Data as ArticleData } from '@root/modules/article/types/article';
import UniversalHeadlinesService from '@root/modules/headlines/services/UniversalHeadlines.service';
import MostReadArticlesService from '@root/modules/headlines/services/MostReadArticles.service';
import AdsLayout from '@root/common/components/layout/AdsLayout.vue';
import ArticleItem from '@root/views/article/components/Article.vue';
import getChannelDomain from '@root/modules/channel/utils/getChannelDomain';
import CategoryHeader from '~/src/views/article/components/multimedija/CategoryHeader.vue';
import Sidebar from '~/src/views/article/components/multimedija/Sidebar.vue';

interface Data {
  loadingFailed: boolean;
  articles: string[];
  nextArticleLoading: boolean;
  previewMode: boolean;
}

interface Computed {
  crArticles: string[];
  activeArticle: ArticleData;
  isVideoArticle: boolean;
  isMultimedijaArticle: boolean;
  disabledAdsList: string[];
}

interface Methods {
  loadNextArticle: () => void;
  waitForNextArticle: (loaded: boolean) => void;
  fetchNextArticles: () => void;
  getSameCategoryArticles: () => Promise<number[]>;
  getMostReadArticles: () => Promise<number[]>;
}

export default Vue.extend<Data, Methods, Computed, unknown>({
  components: {
    AdsLayout,
    ArticleItem,
    CategoryHeader,
    Sidebar,
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('adForm/resetPageAdsSettings');
    next();
  },
  validate({ params }) {
    return /^\d+$/.test(params.id) && /^[a-z0-9-]+$/.test(params.slug);
  },
  data() {
    return {
      loadingFailed: false,
      articles: [this.$route.params.id],
      nextArticleLoading: false,
      previewMode: !!this.$route.query.preview,
    };
  },
  computed: {
    crArticles() {
      return this.$store.getters['article/getCrArticles'];
    },
    activeArticle() {
      return this.$store.getters['article/getActiveArticle'];
    },
    isVideoArticle() {
      const { id } = this.$channelConfig('settings');

      return id === 'video';
    },
    isMultimedijaArticle() {
      const { id } = this.$channelConfig('settings');

      return id === 'multimedija';
    },
    disabledAdsList() {
      return this.$store.getters['adForm/getDisabledAdsList'];
    },
  },
  watch: {
    activeArticle: {
      handler(article) {
        const id = article && String(article.id);

        if (!id) {
          return;
        }

        const isFirstArticle = this.articles.indexOf(id) === 0;

        if (isFirstArticle) {
          if (!this.previewMode) {
            this.fetchNextArticles();
          }
        }
      },
      immediate: true,
    },
  },
  destroyed() {
    this.$store.commit('article/setActiveArticlePath', null);
    this.$store.commit('article/setCrArticles', []);
    window.removeEventListener('scroll', this.loadNextArticle);
  },
  methods: {
    loadNextArticle() {
      const d = document.documentElement;
      const userHasScrolled = window.pageYOffset > 200;
      const offset = window.pageYOffset + (d.clientHeight + 900);
      const height = d.offsetHeight;

      // Load next article
      if (userHasScrolled && offset >= height && !this.nextArticleLoading) {
        const nextArticle = this.crArticles[this.articles.length - 1];
        if (nextArticle) {
          this.nextArticleLoading = true;
          this.articles.push(nextArticle);
        }
      }
    },
    async fetchNextArticles() {
      const articleId = this.$route.params.id;

      const loadNextArticle = () => {
        this.loadNextArticle();
        window.addEventListener('scroll', this.loadNextArticle);
      };

      if (this.crArticles.length !== 0) {
        loadNextArticle();
        return;
      }

      // Get nextArticlesIds from same category
      let nextArticlesIds = await this.getSameCategoryArticles();

      // If category is fresh (less then 2 articles) as fallback fetch most read articles
      if (nextArticlesIds.length < 2 && !this.isMultimedijaArticle) {
        nextArticlesIds = await this.getMostReadArticles();
      }

      // Avoid first article to be duplicated in continuous reading
      const filteredArticlesIds = nextArticlesIds.filter((id) => String(id) !== articleId);

      this.$store.commit('article/setCrArticles', filteredArticlesIds);

      if (filteredArticlesIds.length > 0) {
        loadNextArticle();
      }
    },
    async getSameCategoryArticles() {
      const { id } = this.$channelConfig('settings');
      const universalHeadlinesService = new UniversalHeadlinesService();
      const categoryId = this.activeArticle.primaryCategory.id;
      const variables: any = { limit: 10, categoryId };

      // Exclude video channel articles if current channel is not video
      if (id !== 'video') {
        variables.excludeChannels = ['www.delfi.lt/video'];
      }

      const headlines = await universalHeadlinesService.fetch(variables);
      return headlines.items.map((item) => item.id);
    },
    async getMostReadArticles() {
      const { id } = this.$channelConfig('settings');
      const domain = getChannelDomain(id) || 'www.delfi.lt';
      const mostReadArticlesService = new MostReadArticlesService();
      const headlines = await mostReadArticlesService.fetch({ viewsSince: 'SINCE_24_HOURS', limit: 10, domain });
      return headlines.map((item) => item.id);
    },
    waitForNextArticle(loaded) {
      // If more than 1 article fails to load in a row
      // then don't try to load new articles
      if (!loaded && this.loadingFailed) {
        return;
      }

      this.nextArticleLoading = false;
      this.loadingFailed = !loaded;
    },
  },
});
